import React, { useEffect, useState } from 'react'
import { Badge } from '../../Badge'
import { fetchPromotionOffers, type CleengOffer } from '../../../services/promotionsApi'
import {
  STORE_PLATFORMS,
  derivePlanLabels,
  offerSellsOn,
} from '../../../constants/storePlatforms'
import formStyles from '../../../styles/forms.module.scss'
import styles from './PlanSelectionEditor.module.scss'

// Stored metadata shape — mirrors ACE's hydratePlanSelection
// (apps/ace/internal/service/layout_db.go) and tc.widget.plan_selection.v1.
//
// There is deliberately NO price, currency, discount, or period key. Every
// number on a box resolves server-side from the offer catalog and the live
// coupon, so nothing typed here can change what a viewer pays. What is authored
// is which offers appear, in what order, and the words around them.
//
// Copy comes in two layers: the base strings, plus a `promo` block whose keys
// override their base equivalents while a promotion is running on that offer.
// Each key falls through independently, so a promo block that sets only the
// badge leaves the period label and subtext alone.
// Spec: docs/html/tennischannel/widgets/plan-selection.html
export interface PlanCopyMetadata {
  label?: string
  period_label?: string
  badge?: string
  subtext?: string
}

export interface PlanMetadata extends PlanCopyMetadata {
  offer_code?: string
  highlight?: boolean
  promo?: PlanCopyMetadata
}

export interface PlanSelectionMetadata {
  plans?: PlanMetadata[]
}

interface PlanSelectionEditorProps {
  metadata: PlanSelectionMetadata
  onChange: (metadata: PlanSelectionMetadata) => void
}

/** Offer label for the picker: the catalog title plus its price, so two similarly
 * named offers are still tellable apart. Price is shown for identification only —
 * it is read live at render time, never saved here. */
const offerLabel = (offer: CleengOffer): string => {
  const price =
    offer.price_amount != null
      ? ` — ${offer.price_amount} ${offer.price_currency ?? ''}`.trimEnd()
      : ''
  // An inactive offer renders on no platform at all, so say so in the option
  // itself rather than only in the availability row below.
  const inactive = offer.active ? '' : ' (inactive)'
  return `${offer.title || offer.id}${price}${inactive}`
}

/** Where an offer will and won't render, as a row of all seven store platforms.
 * ACE skips a plan on a store that doesn't sell its offer, so without this a
 * merchandiser only finds the gap by opening the app on that device. */
const PlatformAvailability: React.FC<{ offer: CleengOffer }> = ({ offer }) => {
  return (
    <div className={styles.availability}>
      <ul className={styles.platforms}>
        {STORE_PLATFORMS.map((platform) => {
          const sells = offerSellsOn(offer, platform)
          return (
            <li
              key={platform.code}
              className={sells ? styles.platformOn : styles.platformOff}
              // The glyph is decorative; this is what a screen reader reads.
              aria-label={`${platform.label}: ${sells ? 'available' : 'not available'}`}
            >
              <span aria-hidden="true">{sells ? '✓' : '✕'}</span> {platform.label}
            </li>
          )
        })}
      </ul>
      {/* The badges above already say which platforms are missing. The inactive
          case still needs words: every badge reads ✕ without saying why. */}
      {!offer.active && (
        <p className={formStyles.errorText}>
          This offer is inactive in the billing provider, so it renders on no platform at all.
        </p>
      )}
    </div>
  )
}

const COPY_FIELDS: Array<{
  key: keyof PlanCopyMetadata
  label: string
  basePlaceholder: string
  promoPlaceholder: string
  hint?: string
}> = [
  {
    key: 'label',
    label: 'Plan name',
    basePlaceholder: 'ANNUAL',
    promoPlaceholder: '(same as plan name)',
  },
  {
    key: 'period_label',
    label: 'Period suffix',
    basePlaceholder: '/ year',
    promoPlaceholder: '/ first year',
    hint: 'In promo mode this must describe what the coupon actually does — a coupon carries a discount but no duration, so nothing can check it for you.',
  },
  {
    key: 'badge',
    label: 'Badge',
    basePlaceholder: 'MOST POPULAR',
    promoPlaceholder: 'Limited Time Only - HOT77',
  },
  {
    key: 'subtext',
    label: 'Fine print',
    basePlaceholder: '',
    promoPlaceholder: '*New and returning customers only.',
  },
]

const PlanSelectionEditor: React.FC<PlanSelectionEditorProps> = ({ metadata, onChange }) => {
  const plans = metadata.plans ?? []

  // The live Cleeng catalog, for the offer picker. Same endpoint the promotion
  // editor's picker uses; with no coupon it returns every live offer.
  const [offers, setOffers] = useState<CleengOffer[]>([])
  const [offersError, setOffersError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchPromotionOffers()
      .then((res) => {
        if (!cancelled) setOffers(res.offers)
      })
      .catch(() => {
        // Without the catalog the editor cannot check a code or show where it
        // sells, so it blocks the edit rather than accept an unverified one.
        if (!cancelled) {
          setOffersError(
            'Could not load the live offer list, so no plan can be added or changed. Reload to try again.'
          )
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const updatePlans = (next: PlanMetadata[]) => onChange({ ...metadata, plans: next })

  const patchPlan = (index: number, patch: Partial<PlanMetadata>) =>
    updatePlans(plans.map((plan, i) => (i === index ? { ...plan, ...patch } : plan)))

  const patchPromo = (index: number, patch: Partial<PlanCopyMetadata>) =>
    patchPlan(index, { promo: { ...(plans[index].promo ?? {}), ...patch } })

  // highlight is emphasis AND the initially selected box, so it behaves like a
  // radio: picking one clears the others. ACE rejects a widget with two.
  const setHighlight = (index: number) =>
    updatePlans(plans.map((plan, i) => ({ ...plan, highlight: i === index })))

  const clearHighlight = () => updatePlans(plans.map((plan) => ({ ...plan, highlight: false })))

  const addPlan = () => updatePlans([...plans, { offer_code: '' }])

  const removePlan = (index: number) => updatePlans(plans.filter((_, i) => i !== index))

  const movePlan = (index: number, delta: number) => {
    const target = index + delta
    if (target < 0 || target >= plans.length) return
    const next = [...plans]
    ;[next[index], next[target]] = [next[target], next[index]]
    updatePlans(next)
  }

  const duplicateCodes = new Set(
    plans
      .map((p) => p.offer_code)
      .filter((code, i, all) => code && all.indexOf(code) !== i)
      .filter((code): code is string => Boolean(code))
  )

  // A code the live catalog doesn't carry — a typo, or an offer deactivated
  // since it was authored. ACE skips an unresolvable code rather than failing,
  // so an unnoticed typo shows up only as a box that quietly stopped
  // appearing; this is where it gets caught instead.
  const liveCodes = new Set(offers.map((o) => o.id))
  const isUnknownCode = (code?: string): boolean =>
    offers.length > 0 && Boolean(code) && !liveCodes.has(code as string)

  return (
    <div className={styles.editor}>
      <p className={formStyles.hint}>
        Prices, discounts, and the struck-through “was” price are read live from the billing
        provider — they can’t be typed here, so this form can never change what a viewer pays.
        Order is display order.
      </p>

      {offersError && <p className={formStyles.errorText}>{offersError}</p>}

      {plans.length === 0 && (
        <p className={formStyles.hint}>
          No plans yet. A plan picker with no plans fails to render, so add at least one.
        </p>
      )}

      {plans.map((plan, index) => {
        const isDuplicate = Boolean(plan.offer_code && duplicateCodes.has(plan.offer_code))
        const isUnknown = isUnknownCode(plan.offer_code)
        const hasError = isDuplicate || isUnknown
        const selectedOffer = offers.find((o) => o.id === plan.offer_code)
        // ACE derives the plan name and period suffix from the offer's billing
        // cycle when they are blank. Null for a one-time offer, which has no
        // period to name — then the static placeholders stand in.
        const derived = selectedOffer ? derivePlanLabels(selectedOffer) : null
        const derivedCopy: Partial<Record<keyof PlanCopyMetadata, string>> | null = derived
          ? { label: derived.name, period_label: derived.periodLabel }
          : null
        return (
          <fieldset key={index} className={styles.plan}>
            <legend className={styles.legend}>
              Plan {index + 1}
              {plan.highlight && (
                <Badge variant="secondary" size="small">
                  Highlighted
                </Badge>
              )}
            </legend>

            <div className={formStyles.fieldGroup}>
              <label htmlFor={`plan-offer-${index}`}>Offer</label>
              {/* A picker only. A typed code would skip both the availability
                  row and the unknown-code check, and ACE omits a code it cannot
                  resolve, so a typo would show up only as a missing plan. */}
              <select
                id={`plan-offer-${index}`}
                className={hasError ? formStyles.inputError : undefined}
                value={plan.offer_code ?? ''}
                disabled={offers.length === 0}
                onChange={(e) => patchPlan(index, { offer_code: e.target.value })}
              >
                <option value="">
                  {offers.length === 0 ? 'No offers loaded' : 'Select an offer…'}
                </option>
                {/* Keep an unknown stored code selectable, or the dropdown
                    silently shows blank and the author can't see what is
                    actually saved on the widget. */}
                {isUnknown && (
                  <option value={plan.offer_code}>
                    {plan.offer_code} — not in the live catalog
                  </option>
                )}
                {offers.map((offer) => (
                  <option key={offer.id} value={offer.id}>
                    {offerLabel(offer)}
                  </option>
                ))}
              </select>
              {isUnknown && (
                <p className={formStyles.errorText}>
                  The billing provider has no live offer with this code. It won’t render — ACE
                  skips a plan it can’t resolve rather than failing the whole widget, so this box
                  would just quietly stop appearing. Pick a live offer.
                </p>
              )}
              {selectedOffer && <PlatformAvailability offer={selectedOffer} />}
              {isDuplicate && (
                <p className={formStyles.errorText}>
                  This offer is already on another plan. The offer code is the plan’s identity, so
                  two boxes sharing one would be indistinguishable to a client.
                </p>
              )}
            </div>

            {COPY_FIELDS.map((field) => (
              <div key={field.key} className={formStyles.fieldGroup}>
                <label htmlFor={`plan-${field.key}-${index}`}>
                  {field.label} <Badge variant="secondary" size="small">Public</Badge>
                </label>
                <input
                  id={`plan-${field.key}-${index}`}
                  value={plan[field.key] ?? ''}
                  onChange={(e) => patchPlan(index, { [field.key]: e.target.value })}
                  // Left blank, the plan shows the derived value. Showing that
                  // value as the placeholder tells the merchandiser what blank
                  // means here without storing it.
                  placeholder={derivedCopy?.[field.key] ?? field.basePlaceholder}
                />
                {field.hint && <p className={formStyles.hint}>{field.hint}</p>}
              </div>
            ))}

            <label className={formStyles.checkboxLabel}>
              <input
                type="checkbox"
                checked={Boolean(plan.highlight)}
                onChange={(e) => (e.target.checked ? setHighlight(index) : clearHighlight())}
              />
              Highlight this plan (emphasis, and selected when the page loads)
            </label>

            <details className={styles.promo} open={Boolean(plan.promo)}>
              <summary className={styles.promoSummary}>
                Promotion overrides <Badge variant="secondary" size="small">Public</Badge>
              </summary>
              <p className={formStyles.hint}>
                Used only while a promotion is running on this offer. Blank fields fall through to
                the values above, so you only override what changes. If no promotion is running,
                none of this shows.
              </p>
              {COPY_FIELDS.map((field) => (
                <div key={field.key} className={formStyles.fieldGroup}>
                  <label htmlFor={`plan-promo-${field.key}-${index}`}>{field.label}</label>
                  <input
                    id={`plan-promo-${field.key}-${index}`}
                    value={plan.promo?.[field.key] ?? ''}
                    onChange={(e) => patchPromo(index, { [field.key]: e.target.value })}
                    placeholder={field.promoPlaceholder}
                  />
                </div>
              ))}
            </details>

            <div className={styles.planActions}>
              <button type="button" onClick={() => movePlan(index, -1)} disabled={index === 0}>
                Move up
              </button>
              <button
                type="button"
                onClick={() => movePlan(index, 1)}
                disabled={index === plans.length - 1}
              >
                Move down
              </button>
              <button type="button" onClick={() => removePlan(index)}>
                Remove
              </button>
            </div>
          </fieldset>
        )
      })}

      <button type="button" className={styles.addPlan} onClick={addPlan}>
        Add plan
      </button>
    </div>
  )
}

export default PlanSelectionEditor
